import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Input, Form, Modal, Select, message, Popconfirm, Tag, Row, Col, InputNumber, Card, Rate } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, StarOutlined } from '@ant-design/icons';
import { supplierAPI } from '../../services/api';
import ImportExportButtons from '../../components/ImportExportButtons';

const { Search } = Input;
const { Option } = Select;
const { TextArea } = Input;

const SupplierList = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState({ level: '', type: '', status: '' });
  const [modalVisible, setModalVisible] = useState(false);
  const [ratingModalVisible, setRatingModalVisible] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [form] = Form.useForm();
  const [ratingForm] = Form.useForm();

  // 供应商类型映射
  const typeMap = {
    'manufacturer': '生产厂家',
    'agent': '代理商',
    'distributor': '经销商'
  };

  // 供应商等级颜色
  const levelColors = {
    'A': 'gold',
    'B': 'blue',
    'C': 'green',
    'D': 'default'
  };

  // 状态映射
  const statusMap = {
    'active': { text: '正常', color: 'green' },
    'inactive': { text: '停用', color: 'default' },
    'blacklist': { text: '黑名单', color: 'red' }
  };

  const columns = [
    {
      title: '供应商编码',
      dataIndex: 'code',
      key: 'code',
      width: 120,
      fixed: 'left'
    },
    {
      title: '供应商名称',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      ellipsis: true
    },
    {
      title: '简称',
      dataIndex: 'shortName',
      key: 'shortName',
      width: 100
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (text) => typeMap[text] || text
    },
    {
      title: '等级',
      dataIndex: 'level',
      key: 'level',
      width: 80,
      render: (text) => <Tag color={levelColors[text]}>{text}级</Tag>
    },
    {
      title: '主营类别',
      dataIndex: 'category',
      key: 'category',
      width: 120,
      ellipsis: true
    },
    {
      title: '联系人',
      dataIndex: 'contactPerson',
      key: 'contactPerson',
      width: 100
    },
    {
      title: '联系电话',
      dataIndex: 'contactPhone',
      key: 'contactPhone',
      width: 130
    },
    {
      title: '交货期',
      dataIndex: 'leadTime',
      key: 'leadTime',
      width: 80,
      render: (text) => `${text || 0}天`
    },
    {
      title: '综合评分',
      key: 'rating',
      width: 100,
      render: (_, record) => {
        const avg = ((parseFloat(record.qualityRating) + parseFloat(record.deliveryRating) + parseFloat(record.serviceRating)) / 3).toFixed(1);
        return <Rate disabled allowHalf value={parseFloat(avg)} style={{ fontSize: 12 }} />;
      }
    },
    {
      title: '采购员',
      dataIndex: 'purchasePerson',
      key: 'purchasePerson',
      width: 100,
      render: (purchasePerson) => purchasePerson?.realName || '-'
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (text) => {
        const status = statusMap[text] || { text: text, color: 'default' };
        return <Tag color={status.color}>{status.text}</Tag>;
      }
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Button
            type="link"
            icon={<StarOutlined />}
            onClick={() => handleRating(record)}
          >
            评分
          </Button>
          <Popconfirm
            title="确定要删除这个供应商吗?"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  useEffect(() => {
    fetchSuppliers();
  }, [pagination.current, pagination.pageSize, searchText, filters]);

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      const response = await supplierAPI.getList({
        page: pagination.current,
        limit: pagination.pageSize,
        search: searchText,
        ...filters
      });

      setSuppliers(response.data);
      setPagination(prev => ({
        ...prev,
        total: response.pagination.total
      }));
    } catch (error) {
      message.error('获取供应商列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize
    }));
  };

  const handleEdit = (record) => {
    setEditingSupplier(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleRating = (record) => {
    setEditingSupplier(record);
    ratingForm.setFieldsValue({
      qualityRating: parseFloat(record.qualityRating) || 0,
      deliveryRating: parseFloat(record.deliveryRating) || 0,
      serviceRating: parseFloat(record.serviceRating) || 0
    });
    setRatingModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await supplierAPI.delete(id);
      message.success('删除成功');
      fetchSuppliers();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleAdd = () => {
    setEditingSupplier(null);
    form.resetFields();
    form.setFieldsValue({
      type: 'distributor',
      level: 'C',
      status: 'active',
      paymentMethod: 'transfer',
      creditDays: 0,
      leadTime: 7,
      minOrderAmount: 0
    });
    setModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      if (editingSupplier) {
        await supplierAPI.update(editingSupplier.id, values);
        message.success('更新成功');
      } else {
        await supplierAPI.create(values);
        message.success('创建成功');
      }

      setModalVisible(false);
      fetchSuppliers();
    } catch (error) {
      if (error.errorFields) {
        message.error('请填写完整的表单');
      } else {
        message.error(error.response?.data?.message || '操作失败');
      }
    }
  };

  const handleRatingOk = async () => {
    try {
      const values = await ratingForm.validateFields();
      await supplierAPI.update(editingSupplier.id, values);
      message.success('评分更新成功');
      setRatingModalVisible(false);
      fetchSuppliers();
    } catch (error) {
      message.error('评分更新失败');
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleReset = () => {
    setSearchText('');
    setFilters({ level: '', type: '', status: '' });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  return (
    <div>
      <div className="page-header" style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0 }}>供应商管理</h2>
        <Space>
          <ImportExportButtons
            type="suppliers"
            exportParams={{ search: searchText, ...filters }}
            onImportSuccess={fetchSuppliers}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            新增供应商
          </Button>
        </Space>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col>
            <Search
              placeholder="搜索编码/名称/联系人/电话"
              onSearch={handleSearch}
              style={{ width: 250 }}
              allowClear
            />
          </Col>
          <Col>
            <Select
              placeholder="供应商等级"
              style={{ width: 120 }}
              allowClear
              value={filters.level || undefined}
              onChange={(value) => handleFilterChange('level', value)}
            >
              <Option value="A">A级</Option>
              <Option value="B">B级</Option>
              <Option value="C">C级</Option>
              <Option value="D">D级</Option>
            </Select>
          </Col>
          <Col>
            <Select
              placeholder="供应商类型"
              style={{ width: 120 }}
              allowClear
              value={filters.type || undefined}
              onChange={(value) => handleFilterChange('type', value)}
            >
              <Option value="manufacturer">生产厂家</Option>
              <Option value="agent">代理商</Option>
              <Option value="distributor">经销商</Option>
            </Select>
          </Col>
          <Col>
            <Select
              placeholder="状态"
              style={{ width: 120 }}
              allowClear
              value={filters.status || undefined}
              onChange={(value) => handleFilterChange('status', value)}
            >
              <Option value="active">正常</Option>
              <Option value="inactive">停用</Option>
              <Option value="blacklist">黑名单</Option>
            </Select>
          </Col>
          <Col>
            <Button icon={<ReloadOutlined />} onClick={handleReset}>
              重置
            </Button>
          </Col>
        </Row>
      </Card>

      <Table
        columns={columns}
        dataSource={suppliers}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条记录`
        }}
        onChange={handleTableChange}
        scroll={{ x: 1600 }}
      />

      {/* 编辑/新增供应商弹窗 */}
      <Modal
        title={editingSupplier ? '编辑供应商' : '新增供应商'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => setModalVisible(false)}
        width={800}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="code"
                label="供应商编码"
                rules={[{ required: true, message: '请输入供应商编码' }]}
              >
                <Input placeholder="请输入供应商编码" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="name"
                label="供应商名称"
                rules={[{ required: true, message: '请输入供应商名称' }]}
              >
                <Input placeholder="请输入供应商名称" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="shortName" label="供应商简称">
                <Input placeholder="请输入供应商简称" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="type" label="供应商类型">
                <Select placeholder="请选择供应商类型">
                  <Option value="manufacturer">生产厂家</Option>
                  <Option value="agent">代理商</Option>
                  <Option value="distributor">经销商</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="level" label="供应商等级">
                <Select placeholder="请选择供应商等级">
                  <Option value="A">A级（战略供应商）</Option>
                  <Option value="B">B级（优选供应商）</Option>
                  <Option value="C">C级（合格供应商）</Option>
                  <Option value="D">D级（待考察）</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item name="category" label="主营类别">
                <Input placeholder="如：铣刀、钻头、硬质合金刀具等" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="contactPerson" label="联系人">
                <Input placeholder="请输入联系人" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="contactPhone" label="联系电话">
                <Input placeholder="请输入联系电话" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="contactEmail" label="联系邮箱">
                <Input placeholder="请输入联系邮箱" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="address" label="地址">
            <Input placeholder="请输入详细地址" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="creditDays" label="账期天数">
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  precision={0}
                  addonAfter="天"
                  placeholder="请输入账期天数"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="leadTime" label="交货期">
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  precision={0}
                  addonAfter="天"
                  placeholder="请输入交货期"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="minOrderAmount" label="最小订购金额">
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  precision={2}
                  prefix="¥"
                  placeholder="请输入最小订购金额"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="taxNumber" label="税号">
                <Input placeholder="请输入税号" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="paymentMethod" label="付款方式">
                <Select placeholder="请选择付款方式">
                  <Option value="cash">现金</Option>
                  <Option value="transfer">银行转账</Option>
                  <Option value="check">支票</Option>
                  <Option value="acceptance">承兑汇票</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="bankName" label="开户银行">
                <Input placeholder="请输入开户银行" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="bankAccount" label="银行账号">
                <Input placeholder="请输入银行账号" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="status" label="状态">
                <Select placeholder="请选择状态">
                  <Option value="active">正常</Option>
                  <Option value="inactive">停用</Option>
                  <Option value="blacklist">黑名单</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="remark" label="备注">
            <TextArea rows={3} placeholder="请输入备注信息" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 评分弹窗 */}
      <Modal
        title={`供应商评分 - ${editingSupplier?.name || ''}`}
        open={ratingModalVisible}
        onOk={handleRatingOk}
        onCancel={() => setRatingModalVisible(false)}
        width={400}
        destroyOnClose
      >
        <Form form={ratingForm} layout="vertical">
          <Form.Item name="qualityRating" label="质量评分">
            <Rate allowHalf />
          </Form.Item>
          <Form.Item name="deliveryRating" label="交货评分">
            <Rate allowHalf />
          </Form.Item>
          <Form.Item name="serviceRating" label="服务评分">
            <Rate allowHalf />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SupplierList;
